import React from "react"
import { Helmet } from "react-helmet"
import { useLocation } from "react-router-dom"
import { radius } from '../constants'

// 龍寶寶 隱私權與資料蒐集聲明 — public, no LIFF / auth required.
// Required by the LINE Developers User Data Policy (2.2 / 2.4: must be easily
// viewable to anyone at any time) and by Google AdSense.
//
// Rendered as its own fixed, full-viewport scroll layer so it is unaffected by
// the `.app-shell #root { height:100vh; overflow:hidden }` LIFF shell, and uses
// explicit list markers (the global reset sets `ul { list-style:none }`).
const LAST_UPDATED = "2026-06-17"
const CONTACT_EMAIL = "longbaobao.linebot@gmail.com"

const Section = ({ index, title, children }) => (
  <section style={styles.section}>
    <h2 style={styles.h2}>
      <span style={styles.h2Index}>{index}</span>
      {title}
    </h2>
    {children}
  </section>
)

const Item = ({ children }) => (
  <li style={styles.li}>
    <span style={styles.bullet} />
    <span>{children}</span>
  </li>
)

const PrivacyPage = () => {
  const location = useLocation()
  // Only show a back button when reached in-app (e.g. from 設定), not on the
  // public root URL or for crawlers landing directly on /privacy.
  const showBack = Boolean(location.state && location.state.fromSettings)

  return (
    <div style={styles.viewport}>
      <Helmet>
        <title>龍寶寶 隱私權與資料蒐集聲明</title>
        <meta name="robots" content="index,follow" />
      </Helmet>

      <div style={styles.card}>
        <header style={styles.header}>
          {showBack && (
            <button
              type="button"
              aria-label="返回"
              style={styles.back}
              onClick={() => window.history.back()}
            >
              ‹
            </button>
          )}
          <img
            src={`${process.env.PUBLIC_URL}/龍寶寶_babyDragonHead.jpg`}
            alt="龍寶寶"
            style={styles.logo}
          />
          <h1 style={styles.h1}>隱私權與資料蒐集聲明</h1>
          <div style={styles.updated}>最後更新日期：{LAST_UPDATED}</div>
        </header>

        <div style={styles.bodyText}>
          <p style={styles.lead}>
            「龍寶寶」是一個在 LINE 上幫大家分帳的服務。為了提供分帳與相關功能，我們會蒐集並處理部分使用者資料。
            本聲明說明我們蒐集哪些資料、用途、會分享給誰、保存多久，以及你可以行使的權利。使用本服務即表示你已閱讀並了解本聲明。
          </p>

          <Section index="01" title="我們蒐集的資料">
            <ul style={styles.ul}>
              <Item>LINE 個人資料：使用者 ID、顯示名稱、大頭貼圖片。</Item>
              <Item>群組資料：群組／聊天室 ID、群組名稱、群組成員。</Item>
              <Item>你輸入的分帳內容：費用名稱、金額、分帳對象、帳本資料。</Item>
              <Item>你主動提供的選填資料：匯款資訊（例如銀行帳號），僅在你輸入時才會蒐集。</Item>
              <Item>使用智慧聊天功能時你輸入的訊息。</Item>
            </ul>
            <p style={styles.note}>
              龍寶寶不會蒐集你的 LINE 密碼，也不會讀取你與其他人之間、未提及龍寶寶的私人對話。
            </p>
          </Section>

          <Section index="02" title="使用目的">
            <ul style={styles.ul}>
              <Item>提供分帳、帳本、結算與匯出等核心功能。</Item>
              <Item>顯示成員名稱與大頭貼，讓分帳結果更易讀。</Item>
              <Item>在你啟用時，提供智慧聊天等加值功能（預設關閉）。</Item>
              <Item>維運、除錯與改善服務品質。</Item>
            </ul>
            <p style={styles.note}>我們不會將你的資料用於上述目的以外的用途，也不會販售或購買使用者資料。</p>
          </Section>

          <Section index="03" title="第三方服務">
            <p style={styles.para}>為提供服務，部分資料會傳送給下列協力廠商處理，且僅限於提供服務所必需的範圍：</p>
            <ul style={styles.ul}>
              <Item><strong>LINE</strong>：取得個人資料與收發訊息（LINE Messaging API）。</Item>
              <Item><strong>OpenAI</strong>：當你使用智慧聊天功能時，相關訊息會傳送給 OpenAI 進行處理。</Item>
              <Item><strong>Sentry</strong>：錯誤紀錄與系統穩定性監控。</Item>
              <Item><strong>Google Cloud</strong>：當你使用匯出功能時，產生的帳款檔案會暫存於 Google Cloud 儲存空間。</Item>
            </ul>
          </Section>

          <Section index="04" title="資料保存與刪除">
            <p style={styles.para}>
              為了讓分帳紀錄能持續使用，我們會在你使用服務期間保存上述資料。當你要求刪除、停止使用本服務，
              或將龍寶寶移出群組後，我們會在法律允許的範圍內刪除你的相關資料。你可隨時透過下方聯絡方式
              要求刪除或查詢我們持有的資料。
            </p>
          </Section>

          <Section index="05" title="你的權利">
            <ul style={styles.ul}>
              <Item>查詢我們持有哪些與你相關的資料。</Item>
              <Item>更正不正確的資料。</Item>
              <Item>要求刪除你的資料。</Item>
            </ul>
          </Section>

          <Section index="06" title="聯絡我們">
            <p style={styles.para}>如對本聲明有任何疑問，或要行使上述權利，歡迎來信與我們聯繫：</p>
            <a href={`mailto:${CONTACT_EMAIL}`} style={styles.contactBtn}>{CONTACT_EMAIL}</a>
          </Section>

          <footer style={styles.footer}>© {LAST_UPDATED.slice(0, 4)} 龍寶寶 Dragon Baby</footer>
        </div>
      </div>
    </div>
  )
}

const GREEN = "#126951"
const GOLD = "#C8842A"

const styles = {
  // Fixed, full-viewport scroll layer — escapes the LIFF shell's overflow:hidden.
  viewport: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    background: "linear-gradient(180deg, #0c533c 0%, #0c533c 220px, #eef1ee 220px, #eef1ee 100%)",
    padding: "32px 16px 48px",
    boxSizing: "border-box",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans TC', sans-serif",
  },
  card: {
    maxWidth: "760px",
    margin: "0 auto",
    background: "#ffffff",
    borderRadius: radius.lg,
    boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
    overflow: "hidden",
  },
  header: {
    position: "relative",
    background: `linear-gradient(135deg, ${GREEN} 0%, #0c533c 100%)`,
    color: "#ffffff",
    padding: "36px 32px 28px",
    textAlign: "center",
  },
  back: {
    position: "absolute",
    top: "14px",
    left: "14px",
    width: "34px",
    height: "34px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    borderRadius: radius.circle,
    background: "rgba(255,255,255,0.15)",
    color: "#ffffff",
    fontSize: "24px",
    lineHeight: 1,
    cursor: "pointer",
    padding: 0,
  },
  logo: {
    width: "60px",
    height: "60px",
    borderRadius: radius.circle,
    objectFit: "cover",
    marginBottom: "12px",
    border: "2px solid rgba(255,255,255,0.6)",
  },
  h1: { fontSize: "26px", fontWeight: 600, letterSpacing: "1px", margin: 0 },
  updated: { marginTop: "14px", fontSize: "13px", opacity: 0.75 },
  bodyText: { padding: "8px 32px 32px", color: "#33403b", lineHeight: 1.85 },
  lead: {
    fontSize: "15px",
    color: "#4a554f",
    background: "#f5f8f6",
    borderLeft: `4px solid ${GREEN}`,
    borderRadius: radius.sm,
    padding: "16px 18px",
    margin: "24px 0 8px",
  },
  section: { padding: "20px 0", borderBottom: "1px solid #eceee9" },
  h2: {
    display: "flex",
    alignItems: "center",
    fontSize: "18px",
    fontWeight: 600,
    color: GREEN,
    margin: "0 0 14px",
  },
  h2Index: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "34px",
    height: "26px",
    marginRight: "10px",
    fontSize: "13px",
    fontWeight: 600,
    color: GOLD,
    background: "#fbf6ea",
    borderRadius: "6px",
  },
  para: { fontSize: "15px", margin: "0 0 10px" },
  ul: { margin: "4px 0", padding: 0, listStyle: "none" },
  li: {
    display: "flex",
    alignItems: "flex-start",
    fontSize: "15px",
    margin: "8px 0",
  },
  bullet: {
    flex: "0 0 auto",
    width: "7px",
    height: "7px",
    borderRadius: radius.circle,
    background: GOLD,
    marginTop: "10px",
    marginRight: "12px",
  },
  note: {
    fontSize: "13.5px",
    color: "#8a938d",
    marginTop: "12px",
  },
  contactBtn: {
    display: "inline-block",
    marginTop: "6px",
    padding: "10px 20px",
    background: GREEN,
    color: "#ffffff",
    borderRadius: radius.pill,
    fontSize: "15px",
    fontWeight: 600,
  },
  footer: {
    textAlign: "center",
    color: "#b3bbb5",
    fontSize: "13px",
    paddingTop: "24px",
  },
}

export default PrivacyPage
